import { Carousel } from "@/components/Carousel";
import { PassCards } from "@/components/PassCards";
import { JoinCard } from "@/components/FestivalSidebar";
import { joinStyles } from "@/lib/format";
import { resolveUrl } from "@/lib/api";

export const Section = ({ title, children, testId }) => (
  <section data-testid={testId}>
    <h2 className="text-[20px] md:text-[23px] lg:text-[26px] 4xl:text-[32px] font-bold leading-tight mb-3 md:mb-4">{title}</h2>
    {children}
  </section>
);

const Paragraph = ({ children }) => <p className="text-[15px] 4xl:text-[18px] leading-[1.65] text-[#333] whitespace-pre-line">{children}</p>;

const PersonCard = ({ person }) => (
  <div data-testid="festival-person-card">
    <div className="aspect-[285/405] rounded-xl bg-[#D9D9D9] overflow-hidden">
      {person.image_url && <img src={resolveUrl(person.image_url)} alt={person.name} className="w-full h-full object-cover" loading="lazy" />}
    </div>
    <p className="mt-2.5 text-[14px] font-semibold leading-tight truncate">{person.name}</p>
    <p className="text-[12px] text-[#8A8A8A] font-medium truncate">{person.dance_styles?.length ? joinStyles(person.dance_styles) : person.location}</p>
  </div>
);

const PeopleRow = ({ title, people, testId }) => (
  <Section title={title} testId={testId}>
    <Carousel items={people.map((p, i) => ({ ...p, id: `${p.name}-${i}` }))} render={(p) => <PersonCard person={p} />} perView={{ base: 2, sm: 3, lg: 4, "2xl": 5 }} gap={16} thumbAspect="285 / 405" />
  </Section>
);

export const FestivalBody = ({ festival, videoId, experience }) => {
  const venue = festival.venue || {};
  const hasVenue = venue.name || venue.location || venue.description || venue.image_url;
  return (
    <div className="space-y-10 md:space-y-12">
      {festival.about && (
        <Section title="About This Festival" testId="festival-about">
          <Paragraph>{festival.about}</Paragraph>
        </Section>
      )}
      {experience}
      {festival.bands?.length > 0 && <PeopleRow title="Live Bands Performing This Year" people={festival.bands} testId="festival-bands" />}
      {festival.instructors?.length > 0 && <PeopleRow title="Instructors" people={festival.instructors} testId="festival-instructors" />}
      {hasVenue && (
        <Section title="The Venue" testId="festival-venue">
          <div className="aspect-[16/9] md:aspect-[1132/405] rounded-xl bg-[#D9D9D9] overflow-hidden mb-5">
            {venue.image_url && <img src={resolveUrl(venue.image_url)} alt={venue.name || "Venue"} className="w-full h-full object-cover" loading="lazy" />}
          </div>
          {venue.name && <p className="text-[15px] lg:text-[18px] font-semibold">{venue.name}</p>}
          {venue.location && <p className="text-[13px] lg:text-[15px] text-[#555]">{venue.location}</p>}
          {venue.description && <div className="mt-3"><Paragraph>{venue.description}</Paragraph></div>}
        </Section>
      )}
      {festival.who_for && (
        <Section title="Who is This Festival For?" testId="festival-who-for">
          <Paragraph>{festival.who_for}</Paragraph>
        </Section>
      )}
      {festival.passes?.length > 0 && <PassCards passes={festival.passes} festivalId={festival.id} videoId={videoId} />}
    </div>
  );
};

export const FestivalSection = ({ festival, videoId }) => (
  <div id="festival" className="space-y-10 md:space-y-12 scroll-mt-[90px] lg:scroll-mt-[124px]" data-testid="festival-section">
    {festival.logo_url && (
      <div className="bg-white rounded-2xl py-6 md:py-8 px-6 md:px-10 flex justify-center shadow-[0_2px_12px_rgba(0,0,0,0.03)]" data-testid="festival-logo-banner">
        <img src={resolveUrl(festival.logo_url)} alt={`${festival.name} logo`} className="h-[60px] md:h-[90px] object-contain" />
      </div>
    )}
    <JoinCard festival={festival} />
    <FestivalBody festival={festival} videoId={videoId} />
  </div>
);
